import React, { Component, ErrorInfo } from 'react';

import FlexBox from 'components/FlexBox';
import { getBrowserInfo } from 'lib/helpers';
import { captureException } from 'lib/sentry';
import { copyTextToClipboard } from 'services/clipboard';
import loggerService from 'services/logger';
import { t } from 'ttag';
import Alert from 'ui/Alert/Alert';
import Card from 'ui/card/card';
import PseudoLink from 'ui/pseudolink/pseudolink';

import { Nullable } from '../../types/general';

import css from './ErrorBoundary.css';

const MISSING_ERROR = new Error('Error was swallowed during propagation.');

interface ErrorBoundaryProps {
    withReset?: boolean;
    fallback?: React.ReactNode;
    onError?: (error: Error) => void;
}

interface ErrorBoundaryState {
    error?: Nullable<Error>;
    info?: ErrorInfo;
    isCopied?: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    static displayName = 'ErrorBoundary';
    static browserInfo = getBrowserInfo();

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            error: undefined,
            info: undefined,
            isCopied: false,
        };
    }
    componentDidCatch(err: Nullable<Error>, info: ErrorInfo) {
        const error = err || MISSING_ERROR;
        this.setState({ error, info });
        this.logError(error);
        this.props.onError?.(error);
    }

    logError = (error: Error) => {
        captureException(error);
        loggerService.error('Unhandled error', {
            err: error.message,
            stack: error.stack,
        });
    };

    handleReset = () => {
        this.setState({ error: undefined, info: undefined });
    };

    render() {
        const { error, info, isCopied } = this.state;
        const { withReset = true, fallback } = this.props;

        if (error && fallback !== undefined) {
            return fallback;
        }

        if (error) {
            return (
                <Card className={css.root}>
                    <Alert theme="error" wide>
                        <FlexBox direction="column">
                            <span>{t`Сталася технічна помилка`}</span>
                            <span>
                                {ErrorBoundary.browserInfo.name}{' '}
                                {ErrorBoundary.browserInfo.version} "
                                {error?.message}"
                            </span>
                            <span>
                                {t`Будь ласка, зверніться до технічної підтримки.`}
                                <br />
                                {info && (
                                    <PseudoLink
                                        onClick={() => {
                                            copyTextToClipboard(
                                                JSON.stringify({
                                                    info,
                                                    message: error?.message,
                                                    browser:
                                                        ErrorBoundary.browserInfo,
                                                }),
                                            ).then(() => {
                                                this.setState({
                                                    isCopied: true,
                                                });
                                            });
                                        }}
                                    >
                                        {isCopied
                                            ? t`Деталі помилки скопійовано в буфер обміну`
                                            : t`Скопіювати інформацію про помилку`}
                                    </PseudoLink>
                                )}
                            </span>

                            {withReset && (
                                <PseudoLink
                                    className={css.reloadBtn}
                                    onClick={this.handleReset}
                                >
                                    {t`Перезавантажити сторінку`}
                                </PseudoLink>
                            )}
                        </FlexBox>
                    </Alert>
                </Card>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
